/**
 * AgentTrustCenterPageV2 — unified V2 Trust Center (G301).
 *
 * UNIFICATION (G301): one page covers BOTH internal Agentlets (Lets,
 * AutoLets, SysLets) and external agents (ExteLets) with an in-page
 * scope segmented control (All / Lets / ExteLets). The previous design
 * shipped two scoped wrappers (NativeTrustCenterPage,
 * ExternalTrustCenterPage) which now act as deep links that pre-select
 * the scope rather than rendering separate pages.
 *
 * TAB STRUCTURE (G301):
 *   - Inventory     — trust posture table (the original V2 view)
 *   - Dashboard     — risk distribution, autonomy heatmap, hero KPIs
 *   - Anomalies     — agents on watch (/trust/profiles/watch)
 *   - Board Report  — CISO board pack with DOCX/PPTX export
 *   - Scoring       — per-scope trust scoring config (ORG/TENANT/AGENT)
 *
 * The Slideout (per-agent detail) is shared across every tab, so an
 * anomaly row or dashboard at-risk tile can deep-link into the same
 * timeline / verdicts / forensics view.
 *
 * Backend endpoints wired (all under /api/v1/trust/*):
 *   GET  /dashboard
 *   GET  /profiles · /profiles/{id} · /profiles/watch
 *   POST /profiles/{id}/recalculate
 *   POST + DELETE /profiles/{id}/probation
 *   PUT  /profiles/{id}/autonomy
 *   GET  /profiles/{id}/history · /trends
 *   POST /snapshots
 *   GET  /board-report{,.docx,.pptx}
 *   GET  /scoring-config · /scoring-configs   PUT/DELETE /scoring-config
 *
 * V1 stays as fallback via /trust?legacy=1.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlatform } from '../contexts/PlatformContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import {
  RefreshCw, ShieldCheck, AlertTriangle, Eye, FileText, Sliders, Users,
  BarChart3, Activity, Radio, Share2,
} from 'lucide-react';
import {
  Button, Pill, Card, Slideout, MetricCard, TrustChip, FiveLayerTrace,
} from '../components/v2';
import { AuditHashIntegrity } from '../components/v2/trust';
import {
  InventoryTab, DashboardTab, AnomaliesTab, BoardReportTab,
  TrustTimelineTab, LiveExecutionTab, ScoringConfigTab, TrustGraphTab,
} from '../components/v2/trust/tabs';
import { ProbationModal, AutonomyOverrideModal } from '../components/v2/trust/modals';
import {
  recalculateTrustProfile, triggerProbation, releaseProbation,
  manualAutonomyOverride, verifyAuditIntegrity, downloadAuditCertificate,
  // P0-12 Sprint 2 — Witness anchor status, surfaced inline in the
  // page-header AuditHashIntegrity strip so the Trust Center shows
  // BOTH the chain-integrity verdict AND the external-mirror pill.
  getLatestAuditWitness,
} from '../services/api';

import AgentTrustCenterPage from './AgentTrustCenterPage';
import { trustBand, AT_RISK_BANDS } from '../utils/trustScore';
import { bandTone, bandToneFor, bandLabel, bandMin, displayScore } from '../services/trustBands';
import { deriveAgentArchetype } from '../utils/archetype';
import { TRUST_SCOPES, scopeArchetype } from '../utils/trustScope';

/* Archetype derivation lives in utils/archetype.js (deriveAgentArchetype), which prefers the
 * `archetype` the backend stamps on every agent — Agent.getArchetype(), mirroring the V178
 * agent_archetype() function — and only falls back to heuristics for rows that predate it.
 *
 * This file used to carry its own copy. Its AutoLet test was `name.startsWith('AutoLets-')`, the
 * heuristic Agent.java documents as broken the moment an AutoLet is renamed, so this page and the
 * backend could disagree about what an agent was. The shared helper reads `isRunbookManaged`, the
 * same column the SQL function reads.
 *
 * Band mapping is likewise not defined here: it has one owner in services/trustBands.js. */

/* G301 — Top-level tabs hosted inside the unified shell. Each tab is
 * a separate workspace that operates on the same scope-filtered agent
 * population so toggling the scope updates every surface consistently.
 *
 * G308 + G309 (Phase A) — Trust Timeline and Live Execution tabs added
 * to restore the full V1 surface. All seven tabs are production-grade
 * V2 rebuilds; no V1 component reuse. */
const TOP_TABS = [
  { id: 'inventory',  label: 'Inventory',     icon: Users },
  { id: 'dashboard',  label: 'Dashboard',     icon: BarChart3 },
  { id: 'anomalies',  label: 'Anomalies',     icon: AlertTriangle },
  { id: 'board',      label: 'Board report',  icon: FileText },
  { id: 'timeline',   label: 'Trust timeline',icon: Activity },
  { id: 'live',       label: 'Live Trust Action',icon: Radio },
  { id: 'ontology',   label: 'Trust Ontology',icon: Share2 },
  { id: 'scoring',    label: 'Scoring',       icon: Sliders },
];

/* G301 — Scope segmented control. Maps to the same `agentScope` filter
 * the original component supported, but now operator-toggleable inside
 * the page rather than baked into the route. The scoped routes
 * (/trust/native, /trust/external) still work — they hydrate the
 * initial scope state and the operator can switch from there. */
const SCOPE_OPTIONS = TRUST_SCOPES;

/* G301-G310 — Tab modules live in components/v2/trust/tabs/ — see
 * the imports at the top of this file. Each tab is a self-contained
 * workspace; this shell only orchestrates the scope toggle, the tab
 * router, the shared slideout, and the page-level action modals.
 *
 * If you find a `function DashboardTab` / etc. shim re-introduced
 * here in a future change, it almost certainly should be moved into
 * the tabs/ folder for consistency with the rest of Phase A.
 */

function AgentTrustCenterPageV2Inner({ agentScope }) {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const platform = usePlatform();
  const { agents = [], loadAgents } = platform;
  // RBAC: System agents (SysLets) are Platform-Admin-only. Super Admin and below
  // get their pinned-scope view and must never see platform-owned system agents.
  const { currentRole } = useAuth();
  const isPlatformAdmin = currentRole === 'Platform Admin';

  /* G301 — Scope state. Initialised from the route-level `agentScope`
   * prop (set by NativeTrustCenterPage / ExternalTrustCenterPage) so
   * legacy deep links still land on the right scope, but the user can
   * change it via the segmented control without navigating. */
  /* The scoped routes still hydrate the initial scope. /external-trust maps onto exactly one
   * chip. /native-trust used to mean "Lets and AutoLets together"; now that those are separate
   * chips there is no single chip that means both, so it opens on the whole fleet and the
   * operator picks — which is honest, where landing on either chip alone would quietly hide a
   * category of their agents. */
  const [scope, setScope] = useState(() => (agentScope === 'external' ? 'extelet' : 'all'));
  useEffect(() => {
    if (agentScope === 'external') setScope('extelet');
    else if (agentScope === 'native') setScope('all');
  }, [agentScope]);

  /* G301 — Top-level tab state. URL-restorable via ?tab=anomalies so
   * compliance dashboards and emails can deep-link straight to the
   * board report or scoring config tab. */
  const [topTab, setTopTab] = useState(() => {
    try {
      const u = new URL(window.location.href);
      const t = u.searchParams.get('tab');
      if (t && TOP_TABS.some((x) => x.id === t)) return t;
    } catch (_) { /* SSR safe */ }
    return 'inventory';
  });
  useEffect(() => {
    try {
      const u = new URL(window.location.href);
      if (topTab === 'inventory') u.searchParams.delete('tab');
      else u.searchParams.set('tab', topTab);
      window.history.replaceState({}, '', u.toString());
    } catch (_) { /* non-fatal */ }
  }, [topTab]);

  const reload = useCallback(() => { loadAgents?.(); }, [loadAgents]);
  useEffect(() => { reload(); }, [reload]);

  const enriched = useMemo(() => {
    // DEF-TRUST-DISPLAY-002 — Same precedence fix as DEF-TRUST-DISPLAY-001
    // shipped earlier on AgentsInventoryPageV2.jsx. The legacy chain
    // `trustScore || complianceScore` short-circuited on the @Builder.Default
    // seed value (75.0 native / 50.0 external from Agent.java:228-230)
    // so the runtime-computed complianceScore (updated every 15 min by
    // ComplianceScoreService.calculateScore()) never reached the Trust
    // Center inventory rows OR the band classification. Fix: nullish-
    // coalescing prefers the live runtime number, falls back to the seed
    // only for zero-evaluation agents. Single source of truth keeps the
    // band derivation consistent with the score itself.
    // DEF-TRUST-DISPLAY-003 — Honest Unrated. The runtime complianceScore is the
    // ONLY evidence-backed number. The old seed fallback (trustScore 75/50) made a
    // never-run agent show a confident band — the trust-theater the operator flagged
    // ("215 ExteLets STRONG with 0 runs"). When complianceScore is null the agent has
    // no governed evidence: it is Unrated, full stop. No seed, no number, no band.
    const liveTrust = (a) => (a.complianceScore != null ? a.complianceScore : null);
    const mapped = agents.map(a => {
      const t = liveTrust(a);
      return {
        ...a,
        trust: t,                 // null when unrated
        rated: t != null,
        archetype: deriveAgentArchetype(a),
        band: trustBand(t),       // 'unrated' when t is null
      };
    });
    // System agents (SysLets) are Platform-Admin-only — gate them out for
    // everyone else so Super Admins get only their pinned-scope view.
    const all = isPlatformAdmin
      ? mapped
      : mapped.filter(a => !(a.archetype === 'SysLet' || a.system === true || a.kind === 'system' || a.isSystemAgent === true));
    /* G301 — Scope filter (replaces the prop-only filter). State-driven
     * so the segmented control toggles between Lets, ExteLets, and all
     * agents without leaving the page. */
    // Lets = native, NON-system Agentlets. SysLets are a separate scope (below)
    // so platform system agents never mix into the Lets archetype list.
    const want = scopeArchetype(scope);
    return want === null ? all : all.filter(a => a.archetype === want);
  }, [agents, scope, isPlatformAdmin]);

  /* G304 — Inventory facet / pagination / sort state is owned by the
   * InventoryTab component itself. Keeping that logic out of the shell
   * means the Dashboard / Anomalies / Timeline / Live / Board / Scoring
   * tabs don't pay for filter machinery they don't use, and the shell
   * stays focused on the cross-tab concerns (scope, KPIs, slideout). */

  const [density, setDensity] = useState(() => {
    try { return localStorage.getItem('trust-density') || 'comfortable'; } catch { return 'comfortable'; }
  });
  const cycleDensity = () => {
    const n = density === 'comfortable' ? 'compact' : 'comfortable';
    setDensity(n);
    try { localStorage.setItem('trust-density', n); } catch (_) { /* noop */ }
  };

  const [selected, setSelected] = useState(null);
  const [slideoutTab, setSlideoutTab] = useState('summary');
  // Shared agent focus across the Trust sub-tabs (Ontology / Timeline / Live /
  // Scoring). Each tab initializes its picker from this and writes back on change,
  // so switching tabs preserves the selected agent instead of resetting to the
  // first in the list (the cross-tab reset bug).
  // PROVE7-029 — seed the cross-tab focus from the deep-link ?agentId so the
  // Live Trust Action tab (and every other tab) opens focused on the agent the
  // link came from, instead of the fleet default. Falls back to '' (prior
  // behaviour) when no agentId is present, so non-deep-link nav is unchanged.
  const [focusAgentId, setFocusAgentId] = useState(() => {
    try { return new URL(window.location.href).searchParams.get('agentId') || ''; }
    catch (_) { return ''; }
  });
  const openSlideout = (row) => { setSelected(row); setSlideoutTab('summary'); };
  const closeSlideout = () => setSelected(null);

  /* G307 — Page-level action state. Probation / Autonomy Override
   * modals are hoisted to the shell so any tab that opens the slideout
   * can trigger them via the slideout header buttons without needing
   * its own modal wiring. Recalculate fires inline (no modal needed). */
  const [probationModal, setProbationModal] = useState(null);    // { mode, agent } | null
  const [overrideModal, setOverrideModal]  = useState(null);     // agent | null
  const [recalculating, setRecalculating]  = useState(false);

  const handleRecalculate = async () => {
    if (!selected) return;
    setRecalculating(true);
    try {
      const r = await recalculateTrustProfile(selected.id);
      if (r?.ok === false) {
        addToast(`Recalculate failed: ${r.error || 'unknown error'}`, 'error');
      } else {
        addToast(`Recalculated trust profile for ${selected.name}.`, 'success');
        reload();
      }
    } catch (e) {
      addToast(`Recalculate failed: ${e?.message || e}`, 'error');
    } finally { setRecalculating(false); }
  };

  const handleProbationSubmit = async ({ reason, expiresAt }) => {
    const { mode, agent } = probationModal;
    if (mode === 'place') {
      const r = await triggerProbation(agent.id, reason, expiresAt);
      if (r?.ok === false) throw new Error(r.error || 'Failed');
      addToast(`${agent.name} placed on probation.`, 'success');
    } else {
      const r = await releaseProbation(agent.id, reason);
      if (r?.ok === false) throw new Error(r.error || 'Failed');
      addToast(`${agent.name} released from probation.`, 'success');
    }
    reload();
  };

  const handleOverrideSubmit = async ({ newLevel, reason }) => {
    const r = await manualAutonomyOverride(overrideModal.id, newLevel, reason);
    if (r?.ok === false) throw new Error(r.error || 'Failed');
    addToast(`Autonomy override applied to ${overrideModal.name}.`, 'success');
    reload();
  };

  const selectedOnProbation = !!(selected?.probationUntil || selected?.autonomy === 'PROBATION');

  const kpis = useMemo(() => {
    /* Every rated agent, including one scoring zero. `filter(Boolean)` dropped exactly the
     * agents this strip exists to surface: a 0 is the most serious reading there is, and leaving
     * it out of the fleet average flattered the number by removing the worst case from it. */
    const trustScores = enriched.map(a => a.trust).filter(x => x !== null && x !== undefined);
    const mean = trustScores.length > 0
      ? trustScores.reduce((s, x) => s + x, 0) / trustScores.length
      : null;
    return {
      total: enriched.length,
      rated: trustScores.length,
      // Floored, like every other score on the product. Rounding 84.9 to 85 would move the fleet
      // into Strong on a rounding step.
      avg: mean === null ? 0 : displayScore(mean),
      /* One band set across the product. "At risk" is no longer a band of its own — it is every
       * rated agent below Adequate, which is the question this tile was always asking. */
      strong: enriched.filter(a => a.band === 'strong').length,
      adequate: enriched.filter(a => a.band === 'adequate').length,
      atRisk: enriched.filter(a => AT_RISK_BANDS.includes(a.band)).length,
    };
  }, [enriched]);

  return (
    <div className="min-h-full bg-v2-page text-v2-ink p-4 sm:p-7 max-w-[1500px] mx-auto">
      <header className="flex items-start gap-4 mb-4">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight m-0">Trust Center</h1>
          <p className="text-[13px] text-v2-ink-secondary mt-0.5">
            Unified governance for native Agentlets and external agents — trust posture,
            anomalies, CISO board pack, and scoring policy in one place.
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="ghost" size="sm" iconLeft={<RefreshCw size={13} />} onClick={reload}>Refresh</Button>
          <Button variant="secondary" size="sm" onClick={cycleDensity}>Density: {density.charAt(0).toUpperCase() + density.slice(1)}</Button>
        </div>
      </header>

      {/* P0-12 — Tamper-evident audit log surface. Compact variant lives
          in the page header so the chain-integrity status (and one-click
          certificate download) is always visible — auditors and CISOs no
          longer need to dig into the Trust Timeline tab to see whether
          the audit log is intact. autoVerify runs the verification on
          mount; the operator can re-run or download a fresh certificate
          inline. The 30-day window matches the Trust Center's default
          analysis horizon and the board-report period. */}
      <div className="mb-5 bg-v2-surface border border-v2-border-subtle rounded-v2-md px-4 py-2.5">
        <AuditHashIntegrity
          compact
          autoVerify
          verifyFn={verifyAuditIntegrity}
          downloadFn={downloadAuditCertificate}
          witnessFn={getLatestAuditWitness}
          timeRange="30d"
        />
      </div>

      {/* G301 — Scope segmented control. Renders a 3-way toggle so the
          operator can compare Lets vs ExteLets without leaving the
          page. Keyboard nav: each segment is a real button so Tab +
          Enter selects. Counts derive from `enriched` so the badge
          stays accurate as the underlying agent list refreshes. */}
      <div className="mb-5 flex items-center gap-1 bg-v2-surface border border-v2-border-subtle rounded-v2-md p-1 w-fit"
           role="tablist" aria-label="Trust scope">
        {SCOPE_OPTIONS.filter((opt) => !opt.paOnly || isPlatformAdmin).map((opt) => {
          const active = scope === opt.key;
          // Counts must respect the SysLet RBAC gate: non-PA never sees them.
          const visible = isPlatformAdmin
            ? agents
            : agents.filter(a => deriveAgentArchetype(a) !== 'SysLet');
          const count = opt.archetype === null
            ? visible.length
            : visible.filter(a => deriveAgentArchetype(a) === opt.archetype).length;
          return (
            <button
              key={opt.key}
              role="tab"
              aria-selected={active}
              title={opt.description}
              onClick={() => setScope(opt.key)}
              className={`px-3.5 py-1.5 rounded-v2-sm text-[12.5px] font-medium transition-colors flex items-center gap-2 ${
                active
                  ? 'bg-v2-action text-white shadow-v2-sm'
                  : 'text-v2-ink-secondary hover:text-v2-ink hover:bg-v2-surface-muted'
              }`}
            >
              <span>{opt.label}</span>
              <span className={`text-[10.5px] font-mono-v2 px-1.5 rounded-v2-pill ${
                active ? 'bg-white/20 text-white' : 'bg-v2-surface-muted text-v2-ink-muted'
              }`}>{count}</span>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mb-5">
        {/* Every label and every tone here comes from the one band service. This strip used to
            carry its own 80/60 ramp for the average and paint its "Adequate" tile amber, so the
            same fleet read green on one surface and orange on another. */}
        <MetricCard label="Agents" value={kpis.total} tone="cyan" />
        {/* Nothing rated is an absence, not a critical reading, so it reads muted rather than red. */}
        {/* The coverage sits on the tile, not in a footnote. Two rated agents out of
            twenty-five averaging 100 is an accurate number and a false impression; saying
            "2 of 25 rated" beside it is what stops it reading as the fleet. */}
        <MetricCard label="Trust avg" value={kpis.avg ? `${kpis.avg}` : '—'}
          tone={kpis.avg ? bandTone(kpis.avg) : 'muted'}
          delta={{ value: `${kpis.rated} of ${kpis.total} rated`, direction: 'flat', intent: 'neutral' }} />
        <MetricCard label={`${bandLabel('strong')} (${bandMin('strong')}+)`}
          value={kpis.strong} tone={bandToneFor('strong')} />
        <MetricCard label={`${bandLabel('adequate')} (${bandMin('adequate')}-${bandMin('strong') - 1})`}
          value={kpis.adequate} tone={bandToneFor('adequate')} />
        <MetricCard label={`At risk (under ${bandMin('adequate')})`} value={kpis.atRisk}
          tone={kpis.atRisk > 0 ? 'danger' : 'success'} />
      </div>

      {/* G301 — Top-level tab bar. Inventory is the default workspace;
          Dashboard / Anomalies / Board report / Scoring host the
          surfaces that were missing in V2 (and lived only on the V1
          page). Each tab operates on the same scope-filtered population
          so a "show only ExteLets" toggle propagates through every
          workspace. */}
      <div className="mb-4 border-b border-v2-border-subtle flex items-center gap-1"
           role="tablist" aria-label="Trust Center sections">
        {TOP_TABS.map((tab) => {
          const Icon = tab.icon;
          const active = topTab === tab.id;
          return (
            <button
              key={tab.id}
              role="tab"
              aria-selected={active}
              onClick={() => setTopTab(tab.id)}
              className={`px-3.5 py-2 text-[12.5px] font-medium border-b-2 -mb-px flex items-center gap-1.5 transition-colors ${
                active
                  ? 'border-v2-action text-v2-action-strong'
                  : 'border-transparent text-v2-ink-muted hover:text-v2-ink hover:border-v2-border'
              }`}
            >
              <Icon size={13} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* G304 — Inventory tab (Agent Fleet) — owns its own facets,
          search, sort, pagination, expandable detail rows, and inline
          action modals. */}
      {topTab === 'inventory' && (
        <InventoryTab
          enriched={enriched}
          onAgentUpdated={reload}
          openSlideout={openSlideout}
          addToast={addToast}
          density={density}
        />
      )}

      {/* G305 — Dashboard tab — hero KPIs, risk / autonomy / score
          distributions, recent autonomy transitions table. */}
      {topTab === 'dashboard' && (
        <DashboardTab scope={scope} enriched={enriched} addToast={addToast} />
      )}

      {/* G306 — Anomalies tab — split-view watch list with auto-refresh,
          score sparkline on selection, inline Probation / Override. */}
      {topTab === 'anomalies' && (
        <AnomaliesTab
          scope={scope}
          enriched={enriched}
          openSlideout={openSlideout}
          onAgentUpdated={reload}
          addToast={addToast}
        />
      )}

      {/* G307 — Board Report tab — CISO board pack with TrustGauge,
          executive summary, org goals, top-risk drill, recommendations,
          and DOCX/PPTX/JSON export. */}
      {topTab === 'board' && (
        <BoardReportTab scope={scope} addToast={addToast} />
      )}

      {/* G308 — Trust Timeline tab — agent picker, score waterfall,
          stats strip, audit hash integrity, paginated expandable
          event cards merging autonomy transitions + policy
          enforcements into one chronological feed. */}
      {topTab === 'timeline' && (
        <TrustTimelineTab enriched={enriched} addToast={addToast} focusAgentId={focusAgentId} onFocusAgent={setFocusAgentId} />
      )}

      {/* G309 — Live Execution Center tab — SSE stream of agent
          executions w/ 6-layer policy pipeline visualisation, expandable
          event detail panel, real-time stats. */}
      {topTab === 'live' && (
        <LiveExecutionTab enriched={enriched} addToast={addToast} focusAgentId={focusAgentId} onFocusAgent={setFocusAgentId} />
      )}

      {/* G-4 — Trust Ontology: per-agent radial of the seven proves folded from
          the sealed TrustAssertion stream, with drill-down + action replay. */}
      {topTab === 'ontology' && (
        <TrustGraphTab enriched={enriched} focusAgentId={focusAgentId} onFocusAgent={setFocusAgentId} />
      )}

      {/* G310 — Scoring Config tab — inheritance chain (ORG → TENANT →
          AGENT), full per-scope editor with autonomy threshold visual,
          violation weights, scoring window, org goals, formula
          reference, and delete-override modal. */}
      {topTab === 'scoring' && (
        <ScoringConfigTab enriched={enriched} addToast={addToast} focusAgentId={focusAgentId} onFocusAgent={setFocusAgentId} />
      )}

      <Slideout
        isOpen={!!selected}
        onClose={closeSlideout}
        ariaLabel={selected ? `${selected.name} trust detail` : 'Agent trust detail'}
        width={560}
        tabs={[
          { id: 'summary',   label: 'Summary' },
          { id: 'timeline',  label: 'Trust timeline' },
          { id: 'verdicts',  label: 'Recent verdicts' },
          { id: 'forensics', label: 'Five-Layer trace' },
          { id: 'anomalies', label: 'Anomalies' },
        ]}
        activeTabId={slideoutTab}
        onTabChange={setSlideoutTab}
        header={selected && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Pill tone={selected.archetype === 'ExteLet' ? 'trust' : 'cyan'} size="sm">{selected.archetype}</Pill>
              <Pill tone={bandTone(selected.trust)} size="sm" dot={selected.band !== 'unrated'}>
                {bandLabel(selected.band)}
              </Pill>
            </div>
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <h2 className="text-[18px] font-bold tracking-tight m-0">{selected.name}</h2>
                <p className="text-[12px] text-v2-ink-secondary">{selected.autonomy || selected.autonomyLevel || '—'}</p>
              </div>
              {selected.trust == null
                ? <Pill tone="neutral" size="sm">Unrated</Pill>
                : <TrustChip score={selected.trust} autonomy={selected.autonomy || selected.autonomyLevel} />}
            </div>
            {/* G304/G307 — Shared slideout actions. Any tab that
                opens the slideout (Inventory click, Anomalies click,
                Dashboard top-risk drill, Timeline / Live cross-link)
                gets the same Recalculate / Probation / Override actions
                via the same modals. */}
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <Button variant="primary" size="sm" iconLeft={<Eye size={13} />}
                      onClick={() => navigate(`/agents/${selected.id}`)}>
                Open detail page
              </Button>
              <Button variant="secondary" size="sm" iconLeft={<RefreshCw size={13} className={recalculating ? 'animate-spin' : ''} />}
                      onClick={handleRecalculate} disabled={recalculating}>
                {recalculating ? 'Recalculating…' : 'Recalculate'}
              </Button>
              <Button
                variant={selectedOnProbation ? 'secondary' : 'danger'}
                size="sm"
                onClick={() => setProbationModal({
                  mode: selectedOnProbation ? 'release' : 'place', agent: selected,
                })}
              >
                {selectedOnProbation ? 'Release probation' : 'Place on probation'}
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setOverrideModal(selected)}>
                Override autonomy
              </Button>
            </div>
          </div>
        )}
      >
        {selected && slideoutTab === 'summary' && (
          <Card padding="sm">
            <Card.Header title="Trust summary" />
            <Card.Body>
              <dl className="grid grid-cols-2 gap-3 text-[12.5px]">
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Current trust</dt><dd className="text-v2-ink mt-0.5 font-bold text-[24px]">{selected.trust == null ? <span className="text-v2-ink-muted text-[15px] font-semibold">Unrated</span> : selected.trust}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Trend (24h)</dt><dd className="text-v2-ink mt-0.5">{selected.trustDelta ? (selected.trustDelta > 0 ? `+${selected.trustDelta}` : selected.trustDelta) : '·'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Autonomy</dt><dd className="text-v2-ink mt-0.5">{selected.autonomy || selected.autonomyLevel || '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Phase</dt><dd className="text-v2-ink mt-0.5">{selected.phase || '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Verdicts / 24h</dt><dd className="text-v2-ink mt-0.5">{selected.verdicts24h || '—'}</dd></div>
                <div><dt className="text-v2-ink-muted text-[10.5px] uppercase tracking-wider font-semibold">Blocks / 24h</dt><dd className="text-v2-ink mt-0.5">{selected.blocks24h || 0}</dd></div>
              </dl>
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'timeline' && (
          /* G367 — Was a /trust?legacy=1 bounce. V2 has a dedicated
              Trust Timeline tab (TrustTimelineTab) at the top-level
              Trust Center that fully replaces the V1 timeline. Surface
              that path here instead of redirecting operators back to V1. */
          <Card padding="sm">
            <Card.Header title="Trust score over 30 days" actions={
              <Button variant="ghost" size="sm"
                onClick={() => navigate(`/trust?tab=timeline&agentId=${selected.id}`)}>
                Open in Trust Timeline →
              </Button>
            } />
            <Card.Body>
              <p className="text-v2-ink-muted text-[12.5px]">
                Full timeline rendering — including score waterfall, audit hash-chain integrity,
                and shape-coded event markers — lives in the Trust Center's <strong>Trust Timeline</strong> tab.
              </p>
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'verdicts' && (
          <Card padding="sm">
            <Card.Header title="Recent decisions" />
            <Card.Body>
              {(selected.recentVerdicts || []).length === 0 ? (
                <p className="text-v2-ink-muted text-[12.5px]">No recent decisions recorded.</p>
              ) : (
                <ul className="space-y-2 text-[12.5px]">
                  {(selected.recentVerdicts || []).slice(0, 6).map((v, i) => (
                    <li key={i} className="flex items-baseline gap-2">
                      <Pill tone={v.outcome === 'allow' ? 'success' : v.outcome === 'block' ? 'danger' : 'muted'} size="sm" dot>{v.outcome}</Pill>
                      <span className="text-v2-ink">{v.skillName || v.action || '—'}</span>
                      <span className="ml-auto font-mono-v2 text-[10.5px] text-v2-ink-muted">{v.timestamp ? new Date(v.timestamp).toLocaleTimeString() : '—'}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'forensics' && (
          <Card padding="sm">
            <Card.Header title="Last execution Five-Layer trace" />
            <Card.Body>
              <FiveLayerTrace
                layers={[
                  { layer: 1, verdict: 'allow' },
                  // Below Adequate, not below a literal. The 80 that stood here matched no band
                  // boundary in the product and disagreed with every other surface on this page.
                  { layer: 2, verdict: AT_RISK_BANDS.includes(trustBand(selected.trust)) ? 'block' : 'allow' },
                  { layer: 3, verdict: AT_RISK_BANDS.includes(trustBand(selected.trust)) ? 'skipped' : 'allow' },
                  { layer: 4, verdict: AT_RISK_BANDS.includes(trustBand(selected.trust)) ? 'skipped' : 'allow' },
                  { layer: 5, verdict: 'logged' },
                ]}
              />
            </Card.Body>
          </Card>
        )}
        {selected && slideoutTab === 'anomalies' && (
          <Card padding="sm">
            <Card.Header title="Detected anomalies" />
            <Card.Body>
              {(selected.anomalies || []).length === 0 ? (
                <p className="text-v2-ink-muted text-[12.5px]">No anomalies detected in the current window.</p>
              ) : (
                <ul className="space-y-2 text-[12.5px]">
                  {(selected.anomalies || []).map((a, i) => (
                    <li key={i} className="flex items-baseline gap-2">
                      <AlertTriangle size={12} className="text-v2-warning" />
                      <span className="text-v2-ink">{a.description || a.message}</span>
                    </li>
                  ))}
                </ul>
              )}
            </Card.Body>
          </Card>
        )}
      </Slideout>

      {/* G303 — Page-level action modals. Mounted at the shell so any
          tab can trigger them via the slideout actions above. */}
      {probationModal && (
        <ProbationModal
          isOpen
          mode={probationModal.mode}
          agent={probationModal.agent}
          onClose={() => setProbationModal(null)}
          onSubmit={handleProbationSubmit}
        />
      )}
      {overrideModal && (
        <AutonomyOverrideModal
          isOpen
          agent={overrideModal}
          onClose={() => setOverrideModal(null)}
          onSubmit={handleOverrideSubmit}
        />
      )}
    </div>
  );
}

export default function ResolvedAgentTrustCenterPage(props) {
  /* G133 — pass agentScope through to the inner v2 page so scoped wrappers
   * (NativeTrustCenterPage / ExternalTrustCenterPage) work end-to-end. */
  const url = typeof window !== 'undefined' ? new URL(window.location.href) : null;
  const legacy = url?.searchParams?.get('legacy') === '1';
  if (legacy) return <AgentTrustCenterPage {...props} />;
  return <AgentTrustCenterPageV2Inner {...props} />;
}

export { AgentTrustCenterPageV2Inner };
